<template>
  <div>

    <!-- XOL-464: hoisted to the component root, above every other branch. A
         strict violation can arrive alongside an empty or errored schedule —
         under `errored`, `issues` is [] (nothing set it yet) so this renders
         nothing; under `isEmpty` it can still have something to say, and the
         alternative (leaving it inside the `v-else v-loading` block) meant a
         strict-position violation that emits zero rows landed on isEmpty and
         showed "No fee timeline yet for this sharer." with no explanation at
         all. Costs nothing in the branches where it has nothing to show. -->
    <sharer-billing-issues :issues="issues" />

    <el-alert
      v-if="errored"
      type="error"
      :closable="false"
      show-icon
      title="Couldn't load the fee timeline"
      description="Please try again in a moment." />

    <el-row v-else-if="isEmpty">
      <el-col :span="24" align="center">
        <i class="text-muted">{{ reason || 'No fee timeline yet for this sharer.' }}</i>
      </el-col>
    </el-row>

    <div v-else v-loading="loading">

      <!-- XOL-416/XOL-433: the reason banner. ONE alert above the totals, not a
           marker on each of 228 rows: the condition is a property of the
           agreement, not of any single month.
           Renders the API's `reason` VERBATIM and carries no title of its own.
           It used to be titled "No amount can be calculated for this sharer",
           which was true only for the unpriced case. XOL-433 makes this fire for
           `no_rate` and `no_billing_start` too, where the rows underneath hold
           REAL stored amounts — a heading claiming no amount can be calculated,
           sitting directly above a table of amounts, would contradict itself.
           The API already sends one correct sentence per code; a client-side
           title would be a second source of copy that has to be true for all
           three, which is either vague or wrong for one of them. -->
      <el-alert
        v-if="showReasonBanner"
        type="error"
        :closable="false"
        show-icon
        :title="reason" />

      <!-- Totals header -->
      <el-row :gutter="16" class="timeline-totals">
        <el-col :xs="24" :sm="8" class="timeline-totals__stat">
          <div class="timeline-totals__label">Billed to date</div>
          <div class="timeline-totals__value">{{ totalsDisplay.billedToDate }}</div>
        </el-col>
        <el-col :xs="24" :sm="8" class="timeline-totals__stat">
          <div class="timeline-totals__label timeline-totals__label--muted">Projected remaining</div>
          <div class="timeline-totals__value timeline-totals__value--muted">{{ totalsDisplay.projectedRemaining }}</div>
        </el-col>
        <el-col :xs="24" :sm="8" class="timeline-totals__stat">
          <div class="timeline-totals__label">Lifetime</div>
          <div class="timeline-totals__value timeline-totals__value--bold">{{ totalsDisplay.lifetime }}</div>
        </el-col>
      </el-row>
      <p v-if="isUnpriced" class="timeline-caption text-muted">
        Totals can't be calculated while the Sharer # is 0 — this is not the same as zero.
      </p>
      <!-- XOL-464: a strict violation (position or term) nulls all three totals
           but leaves `reasonCode` null, so `isUnpriced` is false for it — do NOT
           widen `isUnpriced` to cover this. That would print "while the Sharer #
           is 0" on a sharer whose number is perfectly fine, manufacturing the
           exact false statement this branch exists to avoid. Gate on the totals
           themselves instead (`totalsUnknown`), independent of why they're null. -->
      <p v-else-if="totalsUnknown" class="timeline-caption text-muted">
        Totals can't be calculated while any month is unpriced — a dash is not the same as zero.
      </p>
      <p v-else class="timeline-caption text-muted">
        Amounts left of "Lifetime" are billed actuals; "Projected remaining" is forward-estimated and may change.
      </p>

      <!-- Open-ended note -->
      <el-alert
        v-if="openEnded"
        type="info"
        :closable="false"
        show-icon
        title=""
        description="This agreement is open-ended — there's no expiry date, so there's no fixed end to project to. The table runs to the end of next month, or to the end of the sharer rate's term when that is later." />

      <el-table
        :data="displayRows"
        :max-height="520"
        style="width:100%"
        :row-class-name="rowClassName">

        <!-- Expand column -->
        <el-table-column type="expand">
          <template slot-scope="scope">
            <div class="timeline-expand">
              <p class="timeline-expand__caption text-muted">
                Determinant values are shown for explanation only and are never summed.
              </p>

              <el-row :gutter="8">
                <!-- Tiering group -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Tier</span>
                  <span class="timeline-expand__val">{{ scope.row.tier_seq !== null && scope.row.tier_seq !== undefined ? scope.row.tier_seq : '—' }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">{{ positionKeyLabel(scope.row) }}</span>
                  <span class="timeline-expand__val">{{ scope.row._positionLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Billing mode</span>
                  <span class="timeline-expand__val">{{ scope.row.billing_mode !== null && scope.row.billing_mode !== undefined ? scope.row.billing_mode : '—' }}</span>
                </el-col>

                <!-- Fees group -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Telco fee</span>
                  <span class="timeline-expand__val">{{ scope.row._telcoFeeLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">MW rate</span>
                  <span class="timeline-expand__val">{{ scope.row._mwRateLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">RRU rate</span>
                  <span class="timeline-expand__val">{{ scope.row._rruRateLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">RRU set</span>
                  <span class="timeline-expand__val">{{ scope.row.rru_set !== null && scope.row.rru_set !== undefined ? scope.row.rru_set : '—' }}</span>
                </el-col>

                <!-- Sharer-specific group -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">CAPEX</span>
                  <span class="timeline-expand__val">{{ scope.row._capexLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">OPEX</span>
                  <span class="timeline-expand__val">{{ scope.row._opexLabel }}</span>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">Discount</span>
                  <span class="timeline-expand__val">{{ scope.row._discountLabel }}</span>
                </el-col>

                <!-- First-sharer-only -->
                <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                  <span class="timeline-expand__key text-muted">First-sharer-only</span>
                  <span class="timeline-expand__val">{{ scope.row._firstSharerLabel }}</span>
                </el-col>

                <!-- Invoice info (invoiced rows only) -->
                <template v-if="scope.row.status === 'invoiced'">
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Invoice</span>
                    <span class="timeline-expand__val">{{ scope.row.invoice_id !== null && scope.row.invoice_id !== undefined ? scope.row.invoice_id : '—' }}</span>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Invoiced</span>
                    <span class="timeline-expand__val">{{ scope.row.invoiced_at ? formatDate(scope.row.invoiced_at) : '—' }}</span>
                  </el-col>
                </template>

                <!-- Prorated info (prorated rows only) -->
                <template v-if="scope.row.isProrated">
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Charged</span>
                    <span class="timeline-expand__val">{{ scope.row.chargeRangeLabel }}</span>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8" class="timeline-expand__item">
                    <span class="timeline-expand__key text-muted">Full month</span>
                    <span class="timeline-expand__val">{{ scope.row.fullMonthLabel }}</span>
                  </el-col>
                </template>

              </el-row>
            </div>
          </template>
        </el-table-column>

        <!-- Period column -->
        <el-table-column label="Period" min-width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.__boundary" class="timeline-boundary-marker">Projected from here →</span>
            {{ scope.row.periodLabel }}
          </template>
        </el-table-column>

        <!-- Status column -->
        <el-table-column label="Status" width="160">
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.status === 'invoiced'"
              type="success"
              size="small"
              effect="light">
              <i class="fa fa-check-circle"></i> Invoiced
            </el-tag>
            <el-tag
              v-else-if="scope.row.status === 'billed-uninvoiced'"
              type="warning"
              size="small"
              effect="light">
              <i class="fa fa-clock"></i> Billed
            </el-tag>
            <el-tag
              v-else
              type="info"
              size="small"
              effect="light">
              <i class="fa fa-hourglass-half"></i> Projected
            </el-tag>
          </template>
        </el-table-column>

        <!-- Amount column -->
        <el-table-column align="right" class-name="fee-cell" min-width="140">
          <!-- XOL-416: the "Unpriced" marker lives in the HEADER, once, rather than
               beside every dashed cell. Colour is never the only signal — the word,
               the em-dash in each cell and the banner's icon each carry it alone. -->
          <!-- `slot-scope` is required even though the scope is unused: el-table-column
               only picks up the header slot as a SCOPED slot. Without it the slot is
               silently ignored and the column renders with no title at all. -->
          <template slot="header" slot-scope="scope">
            Amount
            <!-- XOL-464: gated on whether any ROW is actually dashed
                 (`anyUnpricedRow`), not on `isUnpriced` — a strict violation
                 dashes the tail of the table via `priced: false` same as the
                 unassigned-Sharer-# case, but `isUnpriced` stays false for it
                 (§ caption above), which used to leave the dashed rows with no
                 column-level marker at all. -->
            <el-tag
              v-if="anyUnpricedRow"
              type="danger"
              size="mini"
              effect="light"
              class="timeline-flag"
              aria-label="Unpriced — no amount can be calculated for one or more months.">Unpriced</el-tag>
          </template>
          <template slot-scope="scope">
            {{ scope.row.amountLabel }}
            <el-tooltip
              v-if="scope.row.isProrated"
              content="Prorated — partial month; see details for the charged range."
              placement="top">
              <el-tag
                type="info"
                size="mini"
                effect="light"
                class="timeline-flag"
                aria-label="Prorated — partial month; see details for the charged range.">
                <i class="fa fa-adjust"></i>
              </el-tag>
            </el-tooltip>
            <el-tooltip
              v-if="scope.row.isLocked"
              content="Rate locked — this month uses a manual override, not the calculated rate."
              placement="top">
              <el-tag
                type="warning"
                size="mini"
                effect="light"
                class="timeline-flag"
                aria-label="Rate locked — this month uses a manual override, not the calculated rate.">
                <i class="fa fa-lock"></i>
              </el-tag>
            </el-tooltip>
          </template>
        </el-table-column>

      </el-table>
    </div>
  </div>
</template>

<script>
import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'
import {toAppTime} from '@/lib/appTime'

import SharerBillingIssues from './SharerBillingIssues'


export default {
  name: 'sharer-fee-timeline',

  mixins: [NumberMixins, PostMixins, DateMixins],

  components: {
    SharerBillingIssues
  },

  props: {
    id: {
      type: [Number, String],
      required: true
    },
    started: {
      type: String,
      default: null
    }
  },

  data: function () {
    return {
      rows: [],
      totals: null,
      window: null,
      licensee: null,
      reason: null,
      // XOL-416: machine-readable sibling of `reason`. Switch on THIS, never on
      // the prose (a literal sentence that changes with any copy edit) and never
      // on the sharer's number — see `isUnpriced`.
      reasonCode: null,
      // XOL-462: strict_position_out_of_range / strict_term_exceeded, absent on
      // any API not carrying the field yet — never render "no issues" from a
      // missing key or a failed fetch.
      issues: [],
      loading: false,
      loaded: false,
      errored: false
    }
  },

  computed: {
    isEmpty: function () {
      return this.loaded && !this.errored && this.rows.length === 0
    },

    openEnded: function () {
      return !!(this.window && this.window.openEnded)
    },

    boundaryIndex: function () {
      for (var i = 0; i < this.rows.length; i++) {
        if (this.rows[i].source === 'projection') {
          return i
        }
      }
      return -1
    },

    hasProjection: function () {
      return this.boundaryIndex >= 0
    },

    displayRows: function () {
      var self = this
      var boundary = self.boundaryIndex
      return self.rows.map(function (row, idx) {
        var periodLabel = ''
        if (row.period) {
          var oPeriod = toAppTime(row.period + '-01')
          periodLabel = (oPeriod === null) ? '' : oPeriod.format('MMM YYYY')
        }

        // XOL-416: an UNPRICED month carries `billed_amount: 0` — the API's honest
        // answer to "what was due this month" for a month that WAS due. Formatting
        // that 0 prints RM0.00, which a finance user reads as "this sharer is free"
        // — the opposite of the truth, which is that we cannot compute the price.
        // So the amount is never the signal; `priced` is.
        //
        // Strictly `=== false`, not falsy: a response predating the flag has no
        // `priced` key at all, and `undefined` must keep formatting normally rather
        // than dashing every historical row.
        var unpricedRow = row.priced === false

        var amountLabel
        if (unpricedRow || row.billed_amount === null || row.billed_amount === undefined) {
          amountLabel = '—'
        } else {
          amountLabel = self.formatMoney(row.billed_amount, 2)
        }

        var fullMonthLabel
        if (unpricedRow || row.full_month_amount === null || row.full_month_amount === undefined) {
          fullMonthLabel = '—'
        } else {
          fullMonthLabel = self.formatMoney(row.full_month_amount, 2)
        }

        var chargeRangeLabel = ''
        if (row.prorated && row.charge_from && row.charge_to) {
          chargeRangeLabel = self.formatDate(row.charge_from) + ' – ' + self.formatDate(row.charge_to)
        }

        // Money determinants
        var _telcoFeeLabel = row.telco_fee !== null && row.telco_fee !== undefined
          ? self.formatMoney(row.telco_fee, 2) : '—'
        var _mwRateLabel = row.mw_rate !== null && row.mw_rate !== undefined
          ? self.formatMoney(row.mw_rate, 2) : '—'
        var _rruRateLabel = row.rru_rate !== null && row.rru_rate !== undefined
          ? self.formatMoney(row.rru_rate, 2) : '—'
        var _capexLabel = row.capex !== null && row.capex !== undefined
          ? self.formatMoney(row.capex, 2) : '—'
        var _opexLabel = row.opex !== null && row.opex !== undefined
          ? self.formatMoney(row.opex, 2) : '—'

        // Discount: % or money
        var _discountLabel
        if (row.discount === null || row.discount === undefined) {
          _discountLabel = '—'
        } else if (row.discount_percentage) {
          _discountLabel = row.discount + '%'
        } else {
          _discountLabel = self.formatMoney(row.discount, 2)
        }

        // First-sharer-only boolean
        var _firstSharerLabel
        if (row.first_sharer_only === null || row.first_sharer_only === undefined) {
          _firstSharerLabel = '—'
        } else {
          _firstSharerLabel = self.formatBoolean(row.first_sharer_only) || 'No'
        }

        // XOL-376 Inc 2: post-clamp billed position, surfaced inline when it differs
        // from the raw position. null billed_position (override) or equal → raw only.
        var _positionLabel
        if (row.position === null || row.position === undefined) {
          _positionLabel = '—'
        } else if (row.billed_position !== null && row.billed_position !== undefined &&
                   row.billed_position !== row.position) {
          _positionLabel = row.position + ' · billed at #' + row.billed_position
        } else {
          _positionLabel = row.position
        }

        return Object.assign({}, row, {
          periodLabel: periodLabel,
          amountLabel: amountLabel,
          fullMonthLabel: fullMonthLabel,
          isProjection: row.source === 'projection',
          isProrated: !!row.prorated,
          isLocked: !!row.rate_locked,
          chargeRangeLabel: chargeRangeLabel,
          __boundary: boundary >= 0 && idx === boundary,
          _telcoFeeLabel: _telcoFeeLabel,
          _mwRateLabel: _mwRateLabel,
          _rruRateLabel: _rruRateLabel,
          _capexLabel: _capexLabel,
          _opexLabel: _opexLabel,
          _discountLabel: _discountLabel,
          _firstSharerLabel: _firstSharerLabel,
          _positionLabel: _positionLabel
        })
      })
    },

    // XOL-416: `unassigned_sharer_no` can arrive WITH a full set of rows — it is a
    // banner over real months, not an empty state. Gate every unpriced affordance
    // on this, and NEVER on the sharer's number: a By-Qty sharer at #0 bills
    // normally from the live sharer count and must render untouched.
    //
    // XOL-464: this stays NARROW on purpose — it drives only the Sharer-#-0
    // caption sentence. A strict violation (position or term) also dashes rows
    // and nulls totals, but through a DIFFERENT reasonCode (or none at all), so
    // `isUnpriced` is false for it. Do not widen this to catch that case: it
    // would print "while the Sharer # is 0" on a sharer whose number is fine,
    // manufacturing the exact false statement this computed exists to avoid.
    // See `totalsUnknown` and `anyUnpricedRow` below for the reason-agnostic
    // gates the strict case actually needs.
    isUnpriced: function () {
      return this.reasonCode === 'unassigned_sharer_no'
    },

    // XOL-464: whether ANY displayed row is dashed, regardless of why. Drives
    // only the column-level "Unpriced" tag in the Amount header — a strict
    // violation dashes the tail of the table through the same `priced: false`
    // grammar as the unassigned-Sharer-# case, but `isUnpriced` (above) stays
    // false for it, so the tag needs its own, broader gate.
    anyUnpricedRow: function () {
      return this.rows.some(function (r) { return r.priced === false })
    },

    // XOL-433: the banner is NOT `isUnpriced`. That computed stays narrow because
    // it drives things that are only true of the unassigned-Sharer-# case
    // specifically — the Sharer-#-0 caption sentence (see `totalsUnknown` and
    // `anyUnpricedRow` for the broader, reason-agnostic gates that drive the
    // column tag and the rest of the caption chain as of XOL-464). The banner is
    // a different question: "did the API tell us something is wrong, on a screen
    // that is showing rows?"
    //
    // Before XOL-433 the API suppressed the rows whenever it sent `no_rate` /
    // `no_billing_start`, so `reason` reached the user through the isEmpty branch
    // at the top of this template. Once those rows are emitted, isEmpty is false
    // and that branch stops rendering — so without this the sentence would be
    // fetched and silently dropped, which is XOL-425's bug on a new path.
    //
    // Requires `reason` as well as the code: an empty red panel explains nothing.
    // On this endpoint the prose predates the code (`reason` shipped in XOL-298,
    // `reasonCode` was added beside it in XOL-416), so code-without-prose is not a
    // response the API produces — no invented fallback copy for a state that does
    // not occur.
    showReasonBanner: function () {
      return !this.isEmpty && !!this.reasonCode && !!this.reason
    },

    totalsDisplay: function () {
      var self = this

      // A null total is UNKNOWN, not zero, whatever produced it. formatMoney would
      // render it RM0.00 (Number(null) is 0), and "Lifetime RM0.00" is a single
      // confident number a finance user acts on.
      //
      // The API sends totals as an object with null FIELDS rather than a null
      // object, deliberately: nulling the container would change the response
      // shape per state and break every consumer's field access. So the old
      // `if (!this.totals)` guard — which only caught an absent object — never
      // fired for the unpriced case and let all three print RM0.00.
      var money = function (value) {
        if (value === null || value === undefined) {
          return '—'
        }
        return self.formatMoney(value, 2)
      }

      if (!this.totals) {
        return { billedToDate: '—', projectedRemaining: '—', lifetime: '—' }
      }
      return {
        billedToDate: money(this.totals.billedToDate),
        projectedRemaining: money(this.totals.projectedRemaining),
        lifetime: money(this.totals.lifetime)
      }
    },

    // XOL-464: a strict violation (position or term) nulls all three totals but
    // leaves `reasonCode` null — so the caption can't key off a reason code the
    // way `isUnpriced` does. This keys off the totals themselves instead: if any
    // of the three is null, something is unknown, whatever produced it.
    //
    // `!t` returning false is deliberate, not an oversight: an absent `totals` is
    // the pre-fetch / legacy shape, already handled by `totalsDisplay`'s own
    // `if (!this.totals)` guard, and must not be reported as "unknown" here too —
    // that would fire this caption on every load before the fetch resolves.
    totalsUnknown: function () {
      var t = this.totals
      if (!t) return false
      return t.billedToDate === null || t.projectedRemaining === null || t.lifetime === null
    }
  },

  created: function () {
    this.load()
  },

  methods: {
    load: function () {
      if (!this.id) {
        this.loaded = true
        return
      }

      var self = this
      this.loading = true
      this.errored = false
      this.loaded = false

      this.fetchData(
        'licensees/' + this.id + '/fee-ledger',
        function (data) {
          var d = data && typeof data === 'object' && !Array.isArray(data) ? data : {}
          self.rows = Array.isArray(d.rows) ? d.rows : []
          self.totals = d.totals || null
          self.window = d.window || null
          self.licensee = d.licensee || null
          self.reason = d.reason || null
          self.reasonCode = d.reasonCode || null
          self.issues = Array.isArray(d.issues) ? d.issues : []
          self.loading = false
          self.loaded = true
        },
        'fee timeline',
        function (_err) {
          self.errored = true
          self.loading = false
          self.loaded = true
          // XOL-464: a REFETCH (not just a first load) can fail after a prior
          // success already populated `issues`. Without this, the hoisted
          // <sharer-billing-issues> banner (root-level, unconditional on
          // `errored`) would keep asserting a specific billing diagnosis
          // sourced from data we just failed to refresh, directly above the
          // "Couldn't load the fee timeline" alert admitting we don't have
          // current data. Clear the state at its source, the same call made
          // for `fromSharerError` / the `strictPosition` watcher elsewhere in
          // this ticket (watcher deleted in XOL-487) — never gate a stale
          // value in the template when it can be cleared where it lives.
          self.issues = []
        }
      )
    },

    // XOL-319: under "Qty" billing the Position determinant is really a sharer
    // count, so label it "Count"; "Position" everywhere else. Case-insensitive
    // so 'Qty'/'qty' both flip.
    positionKeyLabel: function (row) {
      var mode = row && row.billing_mode
      return String(mode).toLowerCase() === 'qty' ? 'Count' : 'Position'
    },

    rowClassName: function (obj) {
      var row = obj.row
      var classes = []
      if (row.isProjection) {
        classes.push('timeline-row--projection')
      }
      if (row.__boundary) {
        classes.push('timeline-row--boundary')
      }
      return classes.join(' ')
    }
  }
}
</script>

<style scoped>
.timeline-totals {
  margin-bottom: 8px;
}

.timeline-totals__stat {
  padding: 8px 0;
}

.timeline-totals__label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 2px;
}

.timeline-totals__label--muted {
  color: #909399;
}

.timeline-totals__value {
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}

.timeline-totals__value--muted {
  color: #909399;
}

.timeline-totals__value--bold {
  font-weight: 600;
}

.timeline-caption {
  font-size: 12px;
  margin-bottom: 8px;
}

.timeline-boundary-marker {
  display: block;
  font-size: 11px;
  color: #909399;
  margin-bottom: 2px;
}

.timeline-flag {
  margin-left: 4px;
  cursor: default;
}

::v-deep .fee-cell {
  font-variant-numeric: tabular-nums;
}

::v-deep .timeline-row--projection td {
  background: #fafafa;
  color: #909399;
}

::v-deep .timeline-row--boundary td {
  border-top: 2px solid #dcdfe6 !important;
}

.timeline-expand {
  padding: 8px 16px;
}

.timeline-expand__caption {
  font-size: 12px;
  margin-bottom: 8px;
}

.timeline-expand__item {
  margin-bottom: 6px;
  display: flex;
  gap: 6px;
}

.timeline-expand__key {
  font-size: 12px;
  min-width: 100px;
  flex-shrink: 0;
}

.timeline-expand__val {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
</style>
